import type { AlarmDto } from '../../../types/dtos/alarm.dto';
import { DeviceDto } from '../../../types/dtos/device.dto';
import { AlarmSeverity, AlarmStatus, AlarmType } from '../../../types/enums/alarm.enum';

type MarkerStyle = {
  color: string;
  iconCode: string;
};

const ALARM_SEVERITY_COLOR_MAP: Record<AlarmSeverity, string> = {
  [AlarmSeverity.CRITICAL]: '#960606',
  [AlarmSeverity.HIGH]: '#ff0000',
  [AlarmSeverity.WARNING]: '#f59e0b',
  [AlarmSeverity.INFO]: '#3b82f6',
};

const ALARM_SEVERITY_PRIORITY_MAP: Record<AlarmSeverity, number> = {
  [AlarmSeverity.CRITICAL]: 5,
  [AlarmSeverity.HIGH]: 4,
  [AlarmSeverity.WARNING]: 3,
  [AlarmSeverity.INFO]: 2,
};

const ALARM_TYPE_ICON_MAP: Record<AlarmType, () => string> = {
  [AlarmType.LOW_BATTERY]: getLowBatteryIcon,
  [AlarmType.LEAK]: getLeakageIcon,
  [AlarmType.DRY]: getLeakageIcon,
  [AlarmType.REVERSE_FLOW]: getLeakageIcon,
  [AlarmType.NO_DATA]: getThresholdExceededIcon,
  [AlarmType.MANIPULATION]: getManipulationIcon,
  [AlarmType.OFFLINE]: getOfflineIcon,
};

const ALARM_TYPE_COLOR_MAP: Record<AlarmType, string> = {
  [AlarmType.LOW_BATTERY]: '#f59e0b',
  [AlarmType.LEAK]: '#0ea5e9',
  [AlarmType.DRY]: '#a16207',
  [AlarmType.REVERSE_FLOW]: '#8b5cf6',
  [AlarmType.NO_DATA]: '#64748b',
  [AlarmType.MANIPULATION]: '#dc2626',
  [AlarmType.OFFLINE]: '#2f67c3',
};

export function getMarkerStyleFromAlarm(alarm: AlarmDto): MarkerStyle {
  const color: string = getAlarmTypeColor(alarm.alarm_type);
  const iconCode: string = getAlarmIconCode(alarm);

  return {
    color,
    iconCode,
  };
}


/*
 * Returns the marker style for the given device.
 * 1: AlarmSeverity.Critical, 2: AlarmSeverity.Warning, 3: Device offline, 4: AlarmSeverity.Info, 5: Normal
 */
export function getMarkerStyle(device: DeviceDto): MarkerStyle {
  const alarm: AlarmDto | null = getHighestPriorityAlarm(device);

  let color: string = 'green';
  let iconCode: string = getOnlineIcon();

  if (alarm && alarm.severity === AlarmSeverity.INFO) {
    color = getAlarmSeverityColor(alarm.severity);
    iconCode = getAlarmIconCode(alarm);
  }

  if (device.status === 'offline') {
    color = ALARM_SEVERITY_COLOR_MAP[AlarmSeverity.HIGH];
    iconCode = getOfflineIcon();
  }

  if (alarm && alarm.severity !== AlarmSeverity.INFO) {
    color = getAlarmSeverityColor(alarm.severity);
    iconCode = getAlarmIconCode(alarm);
  }

  return {
    color,
    iconCode
  };
}

function getHighestPriorityAlarm(device: DeviceDto): AlarmDto | null {
  const alarms: AlarmDto[] = getDeviceAlarms(device)
    .filter((alarm: AlarmDto) => isAlarmVisibleOnMap(alarm));

  if (alarms.length === 0) {
    return null;
  }

  return [...alarms].sort(
    (firstAlarm: AlarmDto, secondAlarm: AlarmDto) => {
      const severityDifference: number =
        getSeverityPriority(secondAlarm.severity) - getSeverityPriority(firstAlarm.severity);

      if (severityDifference !== 0) {
        return severityDifference;
      }

      const dateDifference: number =
        getAlarmTimestamp(secondAlarm) - getAlarmTimestamp(firstAlarm);

      if (dateDifference !== 0) {
        return dateDifference;
      }

      return Number(secondAlarm.id) - Number(firstAlarm.id);
    },
  )[0];
}

export function getActiveAlarmsSorted(device: DeviceDto): AlarmDto[] {
  return (device.alarms ?? [])
    .filter((alarm) => alarm.status !== AlarmStatus.RESOLVED)
    .sort((a, b) => {
      const priorityA: number = getSeverityPriority(a.severity);
      const priorityB: number = getSeverityPriority(b.severity);

      return priorityB - priorityA;
    });
}

function getDeviceAlarms(device: DeviceDto): AlarmDto[] {
  if (device.active_alarms && device.active_alarms.length > 0) {
    return device.active_alarms;
  }

  return device.alarms ?? [];
}

function isAlarmVisibleOnMap(alarm: AlarmDto): boolean {
  return alarm.status !== AlarmStatus.RESOLVED;
}

export function getAlarmSeverityColor(severity: AlarmSeverity): string {
  return ALARM_SEVERITY_COLOR_MAP[severity] ?? 'orange';
}

export function getAlarmTypeColor(alarmType: AlarmType): string {
  return ALARM_TYPE_COLOR_MAP[alarmType] ?? '#ff0000';
}

function getSeverityPriority(severity: AlarmSeverity): number {
  return ALARM_SEVERITY_PRIORITY_MAP[severity] ?? 0;
}

export function getAlarmIconCode(alarm: AlarmDto): string {
  return ALARM_TYPE_ICON_MAP[alarm.alarm_type]?.() ?? getThresholdExceededIcon();
}

function normalizeAlarmType(alarmType: string): string {
  return alarmType
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function getAlarmTimestamp(alarm: AlarmDto): number {
  const dateValue: string | null | undefined =
    alarm.triggered_at
    ?? alarm.created_at
    ?? alarm.updated_at;

  if (!dateValue) {
    return 0;
  }

  const timestamp: number = new Date(dateValue).getTime();

  return Number.isNaN(timestamp)
    ? 0
    : timestamp;
}

function getOnlineIcon(): string {
  return `<path fill="#e3e3e3" d="M298-309 228-380q51-48 116-74t136-26q71 0 136 26t116 74l-70 71q-38-35-84.5-53T480-380q-51 0-97.5 18T298-309ZM73-536 2-607q97-94 220.5-143.5T480-800q134 0 257.5 49.5T958-607l-71 71q-82-79-187-121.5T480-700q-115 0-220 42.5T73-536Zm113 114-70-71q74-71 168-109t197-38q103 0 196.5 37.5T845-494l-70 71q-60-57-136.5-87T480-540q-83 0-158.5 30.5T186-422Zm294 262q-33 0-56.5-23.5T400-240q0-33 23.5-56.5T480-320q33 0 56.5 23.5T560-240q0 33-23.5 56.5T480-160Z"/>`;
}

function getOfflineIcon(): string {
  return `<path fill="#e3e3e3" d="M73-536 2-607q19-18 39.5-34.5T83-673l-53-38 57-81 786 550-57 82-313-219q-6-1-11.5-1H480q-51 0-97.5 18T298-309l-70-71q32-31 70.5-52.5T379-466l-60-42q-37 15-70.5 36.5T186-422l-70-71q25-24 52-44t57-37l-54-38q-26 17-50.5 35.5T73-536Zm407 376q-33 0-56.5-23.5T400-240q0-33 23.5-56.5T480-320q33 0 56.5 23.5T560-240q0 33-23.5 56.5T480-160Zm-79-535-112-78q47-14 94.5-20.5T480-800q134 0 257.5 49.5T958-607l-71 71q-82-79-187-121.5T480-700q-20 0-39.5 1t-39.5 4Zm380 266L479-640q103 0 197.5 37.5T845-494l-64 65Z"/>`;
}

function getLeakageIcon(): string {
  return `<path fill="#e3e3e3" d="M491-200q12 0 20.5-8.5T520-229q0-12-8.5-20.5T491-258q-44 0-75.5-31.5T384-365q0-12-8.5-20.5T355-394q-12 0-20.5 8.5T326-365q0 68 48.5 116.5T491-200Zm-11 120q-137 0-228.5-94T160-408q0-100 79.5-217.5T480-880q161 137 240.5 254.5T800-408q0 140-91.5 234T480-80Zm0-80q104 0 172-70.5T720-408q0-73-60.5-165T480-774Q361-665 300.5-573T240-408q0 107 68 177.5T480-160Zm0-320Z"/>`;
}

function getLowBatteryIcon(): string {
  return `<path fill="#e3e3e3" d="M440-280h80v-80h-80v80Zm0-160h80v-240h-80v240ZM320-80q-33 0-56.5-23.5T240-160v-560q0-33 23.5-56.5T320-800h40v-80h240v80h40q33 0 56.5 23.5T720-720v560q0 33-23.5 56.5T640-80H320Zm0-80h320v-560H320v560Zm0 0v-560 560Z"/>`;
}

function getManipulationIcon(): string {
  return `<path fill="#e3e3e3" d="M481-80q-14 0-27.5-5T429-99L119-409l50-51q9-9 21-13t24-1l146 32v-358q0-17 11.5-28.5T400-840q17 0 28.5 11.5T440-800v440h40v-520q0-17 11.5-28.5T520-920q17 0 28.5 11.5T560-880v520h40v-480q0-17 11.5-28.5T640-880q17 0 28.5 11.5T680-840v480h40v-360q0-17 11.5-28.5T760-760q17 0 28.5 11.5T800-720v500q0 58-41 99T660-80H481Z"/>`;
}

function getThresholdExceededIcon(): string {
  return `<path fill="#e3e3e3" d="M40-120 480-880l440 760H40Zm138-80h604L480-720 178-200Zm302-40q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm-40-120h80v-200h-80v200Zm40-100Z"/>`;
}
